"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getRawTxOptions = exports.ContractInteractor = exports.asRelayCallAbi = void 0;
const bn_js_1 = __importDefault(require("bn.js"));
const ethers_1 = require("ethers");
const ethereumjs_util_1 = require("ethereumjs-util");
const IPaymaster_json_1 = __importDefault(require("./interfaces/IPaymaster.json"));
const IRelayHub_json_1 = __importDefault(require("./interfaces/IRelayHub.json"));
const IForwarder_json_1 = __importDefault(require("./interfaces/IForwarder.json"));
const IStakeManager_json_1 = __importDefault(require("./interfaces/IStakeManager.json"));
const IPenalizer_json_1 = __importDefault(require("./interfaces/IPenalizer.json"));
const IERC2771Recipient_json_1 = __importDefault(require("./interfaces/IERC2771Recipient.json"));
const IRelayRegistrar_json_1 = __importDefault(require("./interfaces/IRelayRegistrar.json"));
const IERC20Token_json_1 = __importDefault(require("./interfaces/IERC20Token.json"));
const VersionsManager_1 = require("./VersionsManager");
const ErrorReplacerJSON_1 = require("./ErrorReplacerJSON");
const Utils_1 = require("./Utils");
const LightTruffleContract_1 = require("./LightTruffleContract");
const Version_1 = require("./Version");
const common_1 = __importDefault(require("@ethereumjs/common"));
const GSNContractsDataTypes_1 = require("./types/GSNContractsDataTypes");
const Utils_js_1 = require("./Utils.js");
const TransactionType_1 = require("./types/TransactionType");
const Constants_1 = require("./Constants");
const MainnetCalldataGasEstimation_1 = require("./environments/MainnetCalldataGasEstimation");
const AsyncZeroAddressCalldataGasEstimation_1 = require("./environments/AsyncZeroAddressCalldataGasEstimation");
const Web3JSUtils_1 = require("./web3js/Web3JSUtils");
const abi_1 = require("@ethersproject/abi");
function asRelayCallAbi(r) {
    return {
        domainSeparatorName: r.metadata.domainSeparatorName,
        relayRequest: r.relayRequest,
        signature: r.metadata.signature,
        approvalData: r.metadata.approvalData,
        maxAcceptanceBudget: r.metadata.maxAcceptanceBudget
    };
}
exports.asRelayCallAbi = asRelayCallAbi;
class ContractInteractor {
    constructor({ maxPageSize, maxPageCount, provider, versionManager, logger, environment, calldataEstimationSlackFactor, domainSeparatorName, deployment = {} }) {
        this.transactionType = TransactionType_1.TransactionType.LEGACY;
        this.maxPageSize = maxPageSize;
        this.maxPageCount = maxPageCount !== null && maxPageCount !== void 0 ? maxPageCount : Number.MAX_SAFE_INTEGER;
        this.logger = logger;
        this.versionManager = versionManager !== null && versionManager !== void 0 ? versionManager : new VersionsManager_1.VersionsManager(Version_1.gsnRuntimeVersion, Version_1.gsnRequiredVersion);
        this.deployment = deployment;
        this.provider = provider;
        this.calldataEstimationSlackFactor = calldataEstimationSlackFactor !== null && calldataEstimationSlackFactor !== void 0 ? calldataEstimationSlackFactor : 1;
        this.lastBlockNumber = 0;
        this.environment = environment;
        this.calculateCalldataGasUsed =
            this.environment.useEstimateGasForCalldataCost
                ? AsyncZeroAddressCalldataGasEstimation_1.AsyncZeroAddressCalldataGasEstimation
                : MainnetCalldataGasEstimation_1.MainnetCalldataGasEstimation;
        this.domainSeparatorName = domainSeparatorName !== null && domainSeparatorName !== void 0 ? domainSeparatorName : '';
        this.IPaymasterContract = (0, LightTruffleContract_1.TruffleContract)({
            contractName: 'IPaymaster',
            abi: IPaymaster_json_1.default
        });
        this.IRelayHubContract = (0, LightTruffleContract_1.TruffleContract)({
            contractName: 'IRelayHub',
            abi: IRelayHub_json_1.default
        });
        this.IForwarderContract = (0, LightTruffleContract_1.TruffleContract)({
            contractName: 'IForwarder',
            abi: IForwarder_json_1.default
        });
        this.IStakeManager = (0, LightTruffleContract_1.TruffleContract)({
            contractName: 'IStakeManager',
            abi: IStakeManager_json_1.default
        });
        this.IPenalizer = (0, LightTruffleContract_1.TruffleContract)({
            contractName: 'IPenalizer',
            abi: IPenalizer_json_1.default
        });
        this.IERC2771Recipient = (0, LightTruffleContract_1.TruffleContract)({
            contractName: 'IERC2771Recipient',
            abi: IERC2771Recipient_json_1.default
        });
        this.IRelayRegistrar = (0, LightTruffleContract_1.TruffleContract)({
            contractName: 'IRelayRegistrar',
            abi: IRelayRegistrar_json_1.default
        });
        this.IERC20Token = (0, LightTruffleContract_1.TruffleContract)({
            contractName: 'IERC20Token',
            abi: IERC20Token_json_1.default
        });
        this.IStakeManager.setProvider(this.provider, undefined);
        this.IRelayHubContract.setProvider(this.provider, undefined);
        this.IPaymasterContract.setProvider(this.provider, undefined);
        this.IForwarderContract.setProvider(this.provider, undefined);
        this.IPenalizer.setProvider(this.provider, undefined);
        this.IERC2771Recipient.setProvider(this.provider, undefined);
        this.IRelayRegistrar.setProvider(this.provider, undefined);
        this.IERC20Token.setProvider(this.provider, undefined);
    }
    async init() {
        const initStartTimestamp = Date.now();
        this.logger.debug('interactor init start');
        if (this.rawTxOptions != null) {
            throw new Error('init was already called');
        }
        const block = await this.provider.getBlock('latest').catch((e) => { throw new Error(`getBlock('latest') failed: ${e.message}\nCheck your internet/ethereum node connection`); });
        if ((block === null || block === void 0 ? void 0 : block.baseFeePerGas) != null) {
            this.logger.info('Network supports Type 2 Transactions (EIP-1559). Checking RPC node \'eth_feeHistory\' method');
            try {
                await this.getFeeHistory('0x1', 'latest', [0.5]);
                this.transactionType = TransactionType_1.TransactionType.TYPE_TWO;
                this.logger.debug('RPC node supports \'eth_feeHistory\'. Initializing to Type 2 Transactions.');
            }
            catch (e) {
                this.logger.warn('Call to \'eth_feeHistory\' failed. Falling back to Legacy Transaction Type.');
                this.transactionType = TransactionType_1.TransactionType.LEGACY;
            }
        }
        await this._resolveDeployment();
        await this._initializeContracts();
        await this._validateCompatibility();
        await this._initializeNetworkParams();
        if (this.relayHubInstance != null) {
            this.relayHubConfiguration = await this.relayHubInstance.getConfiguration();
        }
        this.logger.debug(`client init finished in ${Date.now() - initStartTimestamp} ms`);
        return this;
    }
    async _initializeNetworkParams() {
        this.network = await this.provider.getNetwork();
        this.chainId = parseInt(this.network.chainId.toString());
        this.networkId = await this.provider.send('net_version', []);
        if (this.networkId == null) {
            throw new Error('Failed to query "net_version" from the RPC endpoint');
        }
        this.rawTxOptions = getRawTxOptions(this.chainId, this.networkId, 'private');
    }
    async _resolveDeployment() {
        if (this.deployment.paymasterAddress != null && this.deployment.relayHubAddress != null) {
            this.logger.warn('Already resolved!');
            return;
        }
        if (this.deployment.paymasterAddress != null && this.deployment.paymasterAddress !== '') {
            await this._resolveDeploymentFromPaymaster(this.deployment.paymasterAddress);
        }
        else if (this.deployment.relayHubAddress != null) {
            if (!await this.isContractDeployed(this.deployment.relayHubAddress)) {
                throw new Error(`RelayHub: no contract at address ${this.deployment.relayHubAddress}`);
            }
            // TODO: this branch shouldn't exist as it's only used by the Server and can lead to broken Client configuration
            await this._resolveDeploymentFromRelayHub(this.deployment.relayHubAddress);
        }
        else {
            this.logger.info(`Contract interactor cannot resolve a full deployment from the following input: ${JSON.stringify(this.deployment)}`);
        }
    }
    async _resolveDeploymentFromPaymaster(paymasterAddress) {
        this.paymasterInstance = await this._createPaymaster(paymasterAddress);
        const [relayHubAddress, forwarderAddress, paymasterVersion] = await Promise.all([
            this.paymasterInstance.getRelayHub().catch((e) => { throw new Error(`Not a paymaster contract: ${e.message}`); }),
            this.paymasterInstance.getTrustedForwarder().catch((e) => { throw new Error(`paymaster has no trustedForwarder(): ${e.message}`); }),
            this.paymasterInstance.versionPaymaster().catch((e) => { throw new Error(`Not a paymaster contract: ${e.message}`); }).then((version) => {
                this._validateVersion(version, 'Paymaster');
                return version;
            })
        ]);
        if ((0, Utils_1.isSameAddress)(relayHubAddress, Constants_1.constants.ZERO_ADDRESS) ||
            (0, Utils_1.isSameAddress)(forwarderAddress, Constants_1.constants.ZERO_ADDRESS)) {
            throw new Error(`RelayHub or Forwarder addresses on the provided Paymaster (${paymasterAddress}) are not set. Please set them (for BasePaymaster subclasses use 'setRelayHub' and 'setTrustedForwarder' methods) first and try again.`);
        }
        this.deployment.relayHubAddress = relayHubAddress;
        this.deployment.forwarderAddress = forwarderAddress;
        this.paymasterVersion = paymasterVersion;
        await this._resolveDeploymentFromRelayHub(relayHubAddress);
    }
    async _resolveDeploymentFromRelayHub(relayHubAddress) {
        this.relayHubInstance = await this._createRelayHub(relayHubAddress);
        const [stakeManagerAddress, penalizerAddress, relayRegistrarAddress] = await Promise.all([
            this._hubStakeManagerAddress(),
            this._hubPenalizerAddress(),
            this._hubRelayRegistrarAddress()
        ]);
        this.deployment.relayHubAddress = relayHubAddress;
        this.deployment.stakeManagerAddress = stakeManagerAddress;
        this.deployment.penalizerAddress = penalizerAddress;
        this.deployment.relayRegistrarAddress = relayRegistrarAddress;
    }
    async _validateCompatibility() {
        if (this.deployment == null || this.relayHubInstance == null) {
            return;
        }
        const hub = this.relayHubInstance;
        const version = await hub.versionHub();
        this._validateVersion(version, 'RelayHub');
    }
    _validateVersion(version, contractName) {
        const versionSatisfied = this.versionManager.isRequiredVersionSatisfied(version);
        if (!versionSatisfied) {
            throw new Error(`Provided ${contractName} version(${version}) does not satisfy the requirement(${this.versionManager.requiredVersionRange})`);
        }
    }
    async _validateERC165InterfacesRelay() {
        this.logger.debug(`ERC-165 interface IDs: ${JSON.stringify(Constants_1.erc165Interfaces)}`);
        const pnPromise = this._trySupportsInterface('Penalizer', this.penalizerInstance, Constants_1.erc165Interfaces.penalizer);
        const rrPromise = this._trySupportsInterface('RelayRegistrar', this.relayRegistrar, Constants_1.erc165Interfaces.relayRegistrar);
        const rhPromise = this._trySupportsInterface('RelayHub', this.relayHubInstance, Constants_1.erc165Interfaces.relayHub);
        const smPromise = this._trySupportsInterface('StakeManager', this.stakeManagerInstance, Constants_1.erc165Interfaces.stakeManager);
        const [pn, rr, rh, sm] = await Promise.all([pnPromise, rrPromise, rhPromise, smPromise]);
        const all = pn && rr && rh && sm;
        if (!all) {
            throw new Error(`ERC-165 interface check failed. PN: ${pn} RR: ${rr} RH: ${rh} SM: ${sm}`);
        }
    }
    async _validateERC165InterfacesClient(allowNoPaymaster = false) {
        this.logger.debug(`ERC-165 interface IDs: ${JSON.stringify(Constants_1.erc165Interfaces)}`);
        const fwPromise = this._trySupportsInterface('Forwarder', this.forwarderInstance, Constants_1.erc165Interfaces.forwarder);
        let pmPromise;
        if (allowNoPaymaster && (0, Utils_1.isSameAddress)(this.paymasterInstance.address, Constants_1.constants.ZERO_ADDRESS)) {
            this.logger.debug('skipping ERC-165 check for paymaster: address is not set');
            pmPromise = Promise.resolve(true);
        }
        else {
            pmPromise = this._trySupportsInterface('Paymaster', this.paymasterInstance, Constants_1.erc165Interfaces.paymaster);
        }
        const [fw, pm] = await Promise.all([fwPromise, pmPromise]);
        const all = fw && pm;
        if (!all) {
            throw new Error(`ERC-165 interface check failed. FW: ${fw} PM: ${pm}`);
        }
    }
    async _trySupportsInterface(contractName, contractInstance, interfaceId) {
        if (contractInstance == null) {
            throw new Error(`ERC-165 interface check failed. ${contractName} instance is not initialized`);
        }
        try {
            return await contractInstance.supportsInterface(interfaceId);
        }
        catch (e) {
            const isContractDeployed = await this.isContractDeployed(contractInstance.address);
            throw new Error(`Failed call to ${contractName} supportsInterface at address: ${contractInstance.address} (isContractDeployed: ${isContractDeployed}) with error: ${e.message}`);
        }
    }
    async _initializeContracts() {
        // TODO: do we need all this "differential" deployment ?
        // any sense NOT to initialize some components, or NOT to read them all from the PM and then RH ?
        if (this.relayHubInstance == null && this.deployment.relayHubAddress != null) {
            this.relayHubInstance = await this._createRelayHub(this.deployment.relayHubAddress);
        }
        if (this.relayRegistrar == null && this.deployment.relayRegistrarAddress != null) {
            this.relayRegistrar = await this._createRelayRegistrar(this.deployment.relayRegistrarAddress);
        }
        if (this.paymasterInstance == null && this.deployment.paymasterAddress != null && this.deployment.paymasterAddress !== '') {
            this.paymasterInstance = await this._createPaymaster(this.deployment.paymasterAddress);
        }
        if (this.deployment.forwarderAddress != null) {
            this.forwarderInstance = await this._createForwarder(this.deployment.forwarderAddress);
        }
        if (this.deployment.stakeManagerAddress != null) {
            this.stakeManagerInstance = await this._createStakeManager(this.deployment.stakeManagerAddress);
        }
        if (this.deployment.penalizerAddress != null) {
            this.penalizerInstance = await this._createPenalizer(this.deployment.penalizerAddress);
        }
        if (this.deployment.managerStakeTokenAddress != null) {
            this.erc20Token = await this._createERC20(this.deployment.managerStakeTokenAddress);
        }
    }
    // must use these options when creating Transaction object
    getRawTxOptions() {
        if (this.rawTxOptions == null) {
            throw new Error('_init not called');
        }
        return this.rawTxOptions;
    }
    async _createRecipient(address) {
        if (this.erc2771RecipientInstance != null && this.erc2771RecipientInstance.address.toLowerCase() === address.toLowerCase()) {
            return this.erc2771RecipientInstance;
        }
        this.erc2771RecipientInstance = await this.IERC2771Recipient.at(address);
        return this.erc2771RecipientInstance;
    }
    async _createPaymaster(address) {
        return await this.IPaymasterContract.at(address);
    }
    async _createRelayHub(address) {
        return await this.IRelayHubContract.at(address);
    }
    async _createForwarder(address) {
        return await this.IForwarderContract.at(address);
    }
    async _createStakeManager(address) {
        return await this.IStakeManager.at(address);
    }
    async _createPenalizer(address) {
        return await this.IPenalizer.at(address);
    }
    async _createRelayRegistrar(address) {
        return await this.IRelayRegistrar.at(address);
    }
    async _createERC20(address) {
        return await this.IERC20Token.at(address);
    }
    /**
     * Queries the balance of the token and displays it in a human-readable format, taking 'decimals' into account.
     * Note: does not round up the fraction and truncates it.
     */
    async getTokenBalanceFormatted(address) {
        const balance = await this.erc20Token.balanceOf(address);
        return await this.formatTokenAmount(balance);
    }
    async formatTokenAmount(balance) {
        const { tokenSymbol, tokenDecimals, tokenAddress } = await this.getErc20TokenMetadata();
        return (0, Utils_1.formatTokenAmount)(balance, tokenDecimals, tokenAddress, tokenSymbol);
    }
    async getErc20TokenMetadata() {
        let tokenName;
        try {
            tokenName = await this.erc20Token.name();
        }
        catch (_) {
            tokenName = `ERC-20 token ${this.erc20Token.address}`;
        }
        let tokenSymbol;
        try {
            tokenSymbol = await this.erc20Token.symbol();
        }
        catch (_) {
            tokenSymbol = `ERC-20 token ${this.erc20Token.address}`;
        }
        let tokenDecimals;
        try {
            tokenDecimals = await this.erc20Token.decimals();
        }
        catch (_) {
            tokenDecimals = (0, Web3JSUtils_1.toBN)(0);
        }
        const tokenAddress = this.erc20Token.address;
        return { tokenName, tokenSymbol, tokenAddress, tokenDecimals };
    }
    async isTrustedForwarder(recipientAddress, forwarder) {
        const recipient = await this._createRecipient(recipientAddress);
        return await recipient.isTrustedForwarder(forwarder);
    }
    async getSenderNonce(sender, forwarderAddress) {
        const forwarder = await this._createForwarder(forwarderAddress);
        const nonce = await forwarder.getNonce(sender);
        return nonce.toString();
    }
    async getBlockGasLimit() {
        const latestBlock = await this.provider.getBlock('latest');
        if (latestBlock == null) {
            throw new Error('Failed to query "getBlock", crashing.');
        }
        return parseInt(latestBlock.gasLimit.toString());
    }
    _fixGasFees(relayRequest) {
        if (this.transactionType === TransactionType_1.TransactionType.LEGACY) {
            return { gasPrice: (0, Web3JSUtils_1.toHex)(relayRequest.relayData.maxFeePerGas) };
        }
        else {
            return {
                maxFeePerGas: (0, Web3JSUtils_1.toHex)(relayRequest.relayData.maxFeePerGas),
                maxPriorityFeePerGas: (0, Web3JSUtils_1.toHex)(relayRequest.relayData.maxPriorityFeePerGas)
            };
        }
    }
    /**
     * make a view call to relayCall(), just like the way it will be called by the relayer.
     * returns:
     * - paymasterAccepted - true if accepted
     * - reverted - true if relayCall was reverted.
     * - returnValue - if either reverted or paymaster NOT accepted, then this is the reason string.
     */
    async validateRelayCall(relayCallABIData, viewCallGasLimit, isDryRun) {
        var _a;
        if (viewCallGasLimit == null || relayCallABIData.relayRequest.relayData.maxFeePerGas == null || relayCallABIData.relayRequest.relayData.maxPriorityFeePerGas == null) {
            throw new Error('validateRelayCall: invalid input');
        }
        const relayHub = this.relayHubInstance;
        const from = isDryRun ? Constants_1.constants.DRY_RUN_ADDRESS : relayCallABIData.relayRequest.relayData.relayWorker;
        try {
            const encodedRelayCall = this.encodeABI(relayCallABIData);
            const res = await new Promise((resolve, reject) => {
                const gasFees = this._fixGasFees(relayCallABIData.relayRequest);
                const rpcPayload = [
                    Object.assign({ from, to: relayHub.address, gas: (0, Web3JSUtils_1.toHex)(viewCallGasLimit), data: encodedRelayCall }, gasFees),
                    'latest'
                ];
                this.logger.debug(`Sending in view mode: \n${JSON.stringify(rpcPayload)}\n encoded data: \n${JSON.stringify(relayCallABIData)}`);
                // TODO: this does not seem right....
                const unifiedResErrHandlerFromWeb3Times = (res, err) => {
                    if ((res === null || res === void 0 ? void 0 : res.error) != null) {
                        err = res.error;
                    }
                    const revertMsg = this._decodeRevertFromResponse(err, res);
                    if (revertMsg != null) {
                        reject(new Error(revertMsg));
                    }
                    else if ((0, Utils_1.errorAsBoolean)(err)) {
                        reject(err);
                    }
                    else if ((res === null || res === void 0 ? void 0 : res.result) == null) {
                        reject(new Error('RPC call returned no error and no result'));
                    }
                    else {
                        resolve(res.result);
                    }
                };
                this.provider.send('eth_call', rpcPayload)
                    .then(res => {
                    resolve(res);
                })
                    .catch(err => {
                    // TODO: this function is not very necessary
                    unifiedResErrHandlerFromWeb3Times(undefined, err);
                });
            });
            this.logger.debug('relayCall res=' + res);
            const decoded = new abi_1.AbiCoder().decode(['bool', 'uint256', 'uint256', 'bytes'], res);
            const paymasterAccepted = decoded[0];
            const relayCallStatus = parseInt(decoded[2]);
            let returnValue = decoded[3];
            const recipientReverted = Constants_1.RelayCallStatusCodes.RelayedCallFailed.eqn(relayCallStatus);
            if (!paymasterAccepted || recipientReverted) {
                returnValue = (_a = this._decodeRevertFromResponse({}, { result: returnValue })) !== null && _a !== void 0 ? _a : returnValue;
            }
            return {
                returnValue: returnValue,
                paymasterAccepted,
                recipientReverted,
                relayHubReverted: false
            };
        }
        catch (e) {
            const message = e instanceof Error ? e.message : JSON.stringify(e, ErrorReplacerJSON_1.replaceErrors);
            return {
                paymasterAccepted: false,
                recipientReverted: false,
                relayHubReverted: true,
                returnValue: `view call to 'relayCall' reverted in client: ${message}`
            };
        }
    }
    /**
     * decode revert from rpc response.
     * called from the callback of the provider "eth_call" call.
     * check if response is revert, and extract revert reason from it.
     * support kovan, geth, ganache error formats..
     * @param err - provider err value
     * @param res - provider res value
     */
    // decode revert from rpc response.
    //
    _decodeRevertFromResponse(err, res) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
        let matchGanache = (_b = (_a = err === null || err === void 0 ? void 0 : err.data) === null || _a === void 0 ? void 0 : _a.message) === null || _b === void 0 ? void 0 : _b.toString().match(/: revert(?:ed)? (.*)/);
        if (matchGanache == null) {
            matchGanache = (_d = (_c = res === null || res === void 0 ? void 0 : res.error) === null || _c === void 0 ? void 0 : _c.message) === null || _d === void 0 ? void 0 : _d.toString().match(/: revert(?:ed)? (.*)/);
        }
        if (matchGanache == null) {
            // TODO: investigate other RPC nodes with Ethers.js!
            // ethers.js with hardhat
            matchGanache = (_f = (_e = err === null || err === void 0 ? void 0 : err.error) === null || _e === void 0 ? void 0 : _e.message) === null || _f === void 0 ? void 0 : _f.toString().match(/: revert(?:ed)? (.*)/);
        }
        if (matchGanache == null) {
            // Infura and Alchemy revert message format
            matchGanache = (_g = err === null || err === void 0 ? void 0 : err.message) === null || _g === void 0 ? void 0 : _g.toString().match(/ revert(?:ed):? (.*)/);
        }
        if (matchGanache == null) {
            matchGanache = (_h = err === null || err === void 0 ? void 0 : err.message) === null || _h === void 0 ? void 0 : _h.toString().match(/reverted:(.*)/);
        }
        if (matchGanache != null) {
            // also cleaning up Hardhat Node's verbose revert errors
            return matchGanache[1].replace('with reason string ', '').replace(/^'|'$/g, '');
        }
        const errorData = (_j = err === null || err === void 0 ? void 0 : err.data) !== null && _j !== void 0 ? _j : (_k = res === null || res === void 0 ? void 0 : res.error) === null || _k === void 0 ? void 0 : _k.data;
        const m = errorData === null || errorData === void 0 ? void 0 : errorData.toString().match(/(0x08c379a0\S*)/);
        if (m != null) {
            return (0, Utils_1.decodeRevertReason)(m[1]);
        }
        const result = (_l = res === null || res === void 0 ? void 0 : res.result) !== null && _l !== void 0 ? _l : '';
        if (result.startsWith('0x08c379a0')) {
            return (0, Utils_1.decodeRevertReason)(result);
        }
        return null;
    }
    encodeABI(_) {
        const iface = new abi_1.Interface(this.relayHubInstance.contract.interface.fragments);
        return iface.encodeFunctionData('relayCall', [_.domainSeparatorName, _.maxAcceptanceBudget, _.relayRequest, _.signature, _.approvalData]);
    }
    async getPastEventsForHub(extraTopics, options, names = GSNContractsDataTypes_1.ActiveManagerEvents) {
        return await this._getPastEventsPaginated(this.relayHubInstance.contract, names, extraTopics, options);
    }
    async getPastEventsForRegistrar(extraTopics, options, names = [GSNContractsDataTypes_1.RelayServerRegistered]) {
        return await this._getPastEventsPaginated(this.relayRegistrar.contract, names, extraTopics, options);
    }
    async getPastEventsForStakeManager(names, extraTopics, options) {
        const stakeManager = await this.stakeManagerInstance;
        return await this._getPastEventsPaginated(stakeManager.contract, names, extraTopics, options);
    }
    async getPastEventsForPenalizer(names, extraTopics, options) {
        return await this._getPastEventsPaginated(this.penalizerInstance.contract, names, extraTopics, options);
    }
    getLogsPagesForRange(fromBlock = 1, toBlock) {
        // save 'getBlockNumber' roundtrip for a known max value
        if (this.maxPageSize === Number.MAX_SAFE_INTEGER) {
            return {
                rangeSize: 1,
                pagesForRange: 1
            };
        }
        // noinspection SuspiciousTypeOfGuard - known false positive
        if (typeof fromBlock !== 'number' || typeof toBlock !== 'number') {
            throw new Error(`ContractInteractor:getLogsPagesForRange: [${fromBlock.toString()}..${toBlock === null || toBlock === void 0 ? void 0 : toBlock.toString()}]: only numbers supported when using pagination`);
        }
        const rangeSize = toBlock - fromBlock + 1;
        const pagesForRange = Math.max(Math.ceil(rangeSize / this.maxPageSize), 1);
        if (pagesForRange > 1) {
            this.logger.info(`Splitting request for ${rangeSize} blocks into ${pagesForRange} smaller paginated requests!`);
        }
        return {
            rangeSize,
            pagesForRange
        };
    }
    splitRange(fromBlock, toBlock, parts) {
        if (parts === 1) {
            return [{ fromBlock, toBlock }];
        }
        // noinspection SuspiciousTypeOfGuard - known false positive
        if (typeof fromBlock !== 'number' || typeof toBlock !== 'number') {
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            throw new Error(`ContractInteractor:splitRange: only number supported for block range when using pagination, ${fromBlock} ${toBlock} ${parts}`);
        }
        const rangeSize = toBlock - fromBlock + 1;
        const splitSize = Math.ceil(rangeSize / parts);
        const ret = [];
        for (let b = fromBlock; b <= toBlock; b += splitSize) {
            ret.push({ fromBlock: b, toBlock: Math.min(toBlock, b + splitSize - 1) });
        }
        return ret;
    }
    /**
     * Splits requested range into pages to avoid fetching too many blocks at once.
     * In case 'getLogs' returned with a common error message of "more than X events" dynamically decrease page size.
     */
    async _getPastEventsPaginated(contract, names, extraTopics, options) {
        if (options.toBlock == null) {
            // this is to avoid '!' for TypeScript
            options.toBlock = 'latest';
        }
        if (options.fromBlock == null) {
            options.fromBlock = 1;
        }
        // save 'getBlockNumber' roundtrip for a known max value (must match check in getLogsPagesForRange)
        if (this.maxPageSize !== Number.MAX_SAFE_INTEGER && options.toBlock === 'latest') {
            options.toBlock = await this.getBlockNumber();
            if (options.fromBlock > options.toBlock) {
                options.toBlock = options.fromBlock;
            }
        }
        if (options.fromBlock > options.toBlock) {
            const message = `fromBlock(${options.fromBlock.toString()}) >  
              toBlock(${options.toBlock.toString()})`;
            this.logger.error(message);
            throw new Error(message);
        }
        let { pagesForRange: pagesCurrent, rangeSize } = await this.getLogsPagesForRange(options.fromBlock, options.toBlock);
        if (pagesCurrent > this.maxPageCount) {
            throw new Error(`Failed to make a paginated request to 'getPastEvents' in block range [${options.fromBlock.toString()}..${options.toBlock.toString()}] with page size ${rangeSize}.
This would require ${pagesCurrent} requests, and configured 'pastEventsQueryMaxPageCount' is ${this.maxPageCount}`);
        }
        const relayEventParts = [];
        while (true) {
            const rangeParts = await this.splitRange(options.fromBlock, options.toBlock, pagesCurrent);
            try {
                // eslint-disable-next-line
                for (const { fromBlock, toBlock } of rangeParts) {
                    // this.logger.debug('Getting events from block ' + fromBlock.toString() + ' to ' + toBlock.toString())
                    let attempts = 0;
                    while (true) {
                        try {
                            const pastEvents = await this._getPastEvents(contract, names, extraTopics, Object.assign({}, options, {
                                fromBlock,
                                toBlock
                            }));
                            relayEventParts.push(pastEvents);
                            break;
                        }
                        catch (e) {
                            /* eslint-disable */
                            this.logger.error(`error in getPastEvents. 
              fromBlock: ${fromBlock.toString()} 
              toBlock: ${toBlock.toString()} 
              attempts: ${attempts.toString()}
              names: ${names.toString()}
              extraTopics: ${extraTopics.toString()}
              options: ${JSON.stringify(options)}
              \n${e.toString()}`);
                            /* eslint-enable */
                            attempts++;
                            if (attempts >= 100) {
                                this.logger.error('Too many attempts. throwing ');
                                throw e;
                            }
                            await (0, Utils_js_1.sleep)(300);
                        }
                    }
                }
                break;
            }
            catch (e) {
                // dynamically adjust query size fo some RPC providers
                if (e.toString().match(/query returned more than/) != null) {
                    this.logger.warn('Received "query returned more than X events" error from server, will try to split the request into smaller chunks');
                    if (pagesCurrent > 16) {
                        throw new Error(`Too many events after splitting by ${pagesCurrent}`);
                    }
                    pagesCurrent *= 4;
                }
                else {
                    throw e;
                }
            }
        }
        return relayEventParts.flat();
    }
    async _getPastEvents(contract, names, extraTopics, options) {
        const topics = [];
        const eventTopic = (0, Utils_1.event2topic)(contract, names);
        topics.push(eventTopic);
        // TODO: AFAIK this means only the first parameter of the event is supported
        if (extraTopics.length > 0) {
            topics.push(...extraTopics);
        }
        const logs = await this.provider.getLogs({
            fromBlock: options.fromBlock,
            toBlock: options.toBlock,
            address: contract.address,
            topics: topics
        });
        return logs.map(it => { return Object.assign(it, contract.interface.parseLog(it)); });
    }
    async getBalance(address, defaultBlock = 'latest') {
        return (await this.provider.getBalance(address, defaultBlock)).toString();
    }
    async getBlockNumberRightNow() {
        return await this.provider.getBlockNumber();
    }
    async getBlockNumber() {
        let blockNumber = -1;
        let attempts = 0;
        while (blockNumber < this.lastBlockNumber && attempts <= 10) {
            try {
                blockNumber = await this.getBlockNumberRightNow();
            }
            catch (e) {
                this.logger.error(`getBlockNumber: ${e.message}`);
            }
            if (blockNumber >= this.lastBlockNumber) {
                break;
            }
            await (0, Utils_js_1.sleep)(1000);
            attempts++;
        }
        if (blockNumber < this.lastBlockNumber) {
            throw new Error(`couldn't retrieve latest blockNumber from node. last block: ${this.lastBlockNumber}, got block: ${blockNumber}`);
        }
        this.lastBlockNumber = blockNumber;
        return blockNumber;
    }
    async sendSignedTransaction(rawTx) {
        // noinspection ES6RedundantAwait - PromiEvent makes lint less happy about this line
        return await this.provider.sendTransaction(rawTx);
    }
    async estimateGas(transactionDetails) {
        let estimation = await this.provider.estimateGas(transactionDetails);
        // there is a bug that breaks tests that rely on exact gas limit:
        // https://ethereum.stackexchange.com/questions/20878/why-estimategas-return-21001-instead-21000
        if (parseInt(estimation.toString()) === 21001) {
            estimation = 21000;
        }
        return parseInt(estimation.toString());
    }
    /**
     * In order to calculate the inner transaction gas limit we perform an 'estimateGas' from the Forwarder address.
     * As the calldata is already on-chain, the is no need to include its cost in the gas limit, so we subtract it.
     */
    async estimateInnerCallGasLimit(gsnTransactionDetails) {
        const originalGasEstimation = await this.estimateGas(gsnTransactionDetails);
        // note: overriding 'calldataEstimationSlackFactor' here as this value is used for inner call gas limit
        // and therefore there is no need to add extra factor even in case of L2s
        const calldataGasCost = await this.calculateCalldataGasUsed(gsnTransactionDetails.data, this.environment, 1, this.provider);
        const adjustedEstimation = originalGasEstimation - calldataGasCost;
        this.logger.debug(`estimateGasWithoutCalldata: original estimation: ${originalGasEstimation}; calldata cost: ${calldataGasCost}; adjusted estimation: ${adjustedEstimation}`);
        if (adjustedEstimation < 0) {
            throw new Error(`estimateGasWithoutCalldata: calldataGasCost(${calldataGasCost}) exceeded originalGasEstimation(${originalGasEstimation})`);
        }
        return adjustedEstimation;
    }
    async getGasAndDataLimitsFromPaymaster(paymaster) {
        try {
            const paymasterContract = await this._createPaymaster(paymaster);
            return await paymasterContract.getGasAndDataLimits();
        }
        catch (e) {
            const error = e;
            let message = `unknown paymaster error: ${error.message}`;
            if ((e === null || e === void 0 ? void 0 : e.code) === 'CALL_EXCEPTION' || // ethers.js does not differentiate revert reasons
                error.message.includes('Returned values aren\'t valid, did it run Out of Gas?')) {
                message = `not a valid paymaster contract: ${paymaster}`;
            }
            else if (error.message.includes('no code at address')) {
                message = `'non-existent paymaster contract: ${paymaster}`;
            }
            throw new Error(message);
        }
    }
    /**
     * @returns result - maximum possible gas consumption by this relayed call
     * (calculated on chain by RelayHub.verifyGasAndDataLimits)
     */
    async calculateTransactionMaxPossibleGas(_) {
        const msgDataLength = (0, ethereumjs_util_1.toBuffer)(_.msgData).length;
        const msgDataGasCostInsideTransaction = (0, Web3JSUtils_1.toBN)(this.environment.dataOnChainHandlingGasCostPerByte)
            .muln(msgDataLength)
            .toNumber();
        const calldataCost = await this.calculateCalldataGasUsed(_.msgData, this.environment, this.calldataEstimationSlackFactor, this.provider);
        const result = (0, Utils_1.toNumber)(this.relayHubConfiguration.gasOverhead) +
            msgDataGasCostInsideTransaction +
            calldataCost +
            parseInt(_.relayCallGasLimit) +
            (0, Utils_1.toNumber)(_.gasAndDataLimits.preRelayedCallGasLimit) +
            (0, Utils_1.toNumber)(_.gasAndDataLimits.postRelayedCallGasLimit);
        this.logger.debug(`
input:\n${JSON.stringify(_)}
msgDataLength: ${msgDataLength}
calldataCost: ${calldataCost}
msgDataGasCostInsideTransaction: ${msgDataGasCostInsideTransaction}
environment: ${JSON.stringify(this.environment)}
relayHubConfiguration: ${JSON.stringify(this.relayHubConfiguration)}
calculateTransactionMaxPossibleGas: result: ${result}
`);
        return result;
    }
    /**
     * Only used by the RelayClient.
     * @param relayRequestOriginal request input of the 'relayCall' method with some fields not yet initialized
     * @param variableFieldSizes configurable sizes of 'relayCall' parameters with variable size types
     * @return {PrefixedHexString} top boundary estimation of how much gas sending this data will consume
     */
    async estimateCalldataCostForRequest(relayRequestOriginal, variableFieldSizes) {
        // protecting the original object from temporary modifications done here
        const relayRequest = Object.assign({}, relayRequestOriginal, {
            relayData: Object.assign({}, relayRequestOriginal.relayData)
        });
        relayRequest.relayData.transactionCalldataGasUsed = '0xffffffffff';
        relayRequest.relayData.paymasterData = '0x' + 'ff'.repeat(variableFieldSizes.maxPaymasterDataLength);
        const maxAcceptanceBudget = '0xffffffffff';
        const signature = '0x' + 'ff'.repeat(65);
        const approvalData = '0x' + 'ff'.repeat(variableFieldSizes.maxApprovalDataLength);
        const encodedData = this.encodeABI({
            domainSeparatorName: this.domainSeparatorName,
            relayRequest,
            signature,
            approvalData,
            maxAcceptanceBudget
        });
        const calculatedCalldataGasUsed = await this.calculateCalldataGasUsed(encodedData, this.environment, this.calldataEstimationSlackFactor, this.provider);
        return `0x${calculatedCalldataGasUsed.toString(16)}`;
    }
    // note: when passing 'gasPrice' to a view call, the sender balance is checked to have sufficient balance
    // we must set 'from' and 'gas' parameters as well to avoid failing a view call with 'insufficient gas'
    async calculatePaymasterGasAndDataLimits(relayTransactionRequest, gasAndDataLimits, gasReserve, gasFactor, viewCallFrom, viewCallGasLimit) {
        if (gasAndDataLimits == null) {
            gasAndDataLimits = await this.getGasAndDataLimitsFromPaymaster(relayTransactionRequest.relayRequest.relayData.paymaster);
        }
        const { paymasterAcceptanceBudget, effectiveAcceptanceBudget, transactionCalldataGasUsed, maxPossibleGas } = await this.calculateRequestLimits(relayTransactionRequest, gasAndDataLimits);
        const maxPossibleGasFactorReserve = gasReserve + Math.floor(maxPossibleGas * gasFactor);
        // removing Ethers 'signer' to allow overriding 'from' address
        // note that it will remove the mapping of types to BN!
        const viewOnlyHub = this.relayHubInstance.contract.connect(this.provider);
        const maxPossibleCharge = await viewOnlyHub.calculateCharge(maxPossibleGasFactorReserve, relayTransactionRequest.relayRequest.relayData, {
            from: viewCallFrom,
            gasLimit: viewCallGasLimit,
            gasPrice: relayTransactionRequest.relayRequest.relayData.maxFeePerGas
        });
        return {
            paymasterAcceptanceBudget,
            effectiveAcceptanceBudget,
            transactionCalldataGasUsed,
            maxPossibleCharge: (0, Web3JSUtils_1.toBN)(maxPossibleCharge.toString()),
            maxPossibleGas: maxPossibleGasFactorReserve
        };
    }
    async calculateRequestLimits(relayTransactionRequest, gasAndDataLimits) {
        const relayCallAbiInput = {
            domainSeparatorName: relayTransactionRequest.metadata.domainSeparatorName,
            maxAcceptanceBudget: '0xffffffff',
            relayRequest: relayTransactionRequest.relayRequest,
            signature: relayTransactionRequest.metadata.signature,
            approvalData: relayTransactionRequest.metadata.approvalData
        };
        const msgData = this.encodeABI(relayCallAbiInput);
        const transactionCalldataGasUsed = await this.calculateCalldataGasUsed(msgData, this.environment, this.calldataEstimationSlackFactor, this.provider);
        const maxPossibleGas = await this.calculateTransactionMaxPossibleGas({
            msgData,
            gasAndDataLimits,
            relayCallGasLimit: relayTransactionRequest.relayRequest.request.gas
        });
        const paymasterAcceptanceBudget = gasAndDataLimits.acceptanceBudget.toNumber();
        const effectiveAcceptanceBudget = paymasterAcceptanceBudget + transactionCalldataGasUsed + transactionCalldataGasUsed;
        return {
            transactionCalldataGasUsed,
            maxPossibleGas,
            paymasterAcceptanceBudget,
            effectiveAcceptanceBudget
        };
    }
    // TODO: !This method is not really necessary - only used for a fraction of transactions. Replace with 'getGasFees'.
    // TODO: cache response for some time to optimize. It doesn't make sense to optimize these requests in calling code.
    async getGasPrice() {
        // TODO: this still uses pre-1559 gas value which may be a problem.
        const gasPriceFromNode = await this.provider.perform('getGasPrice', []);
        if (gasPriceFromNode == null) {
            throw new Error('getGasPrice: node returned null value');
        }
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (!this.environment.getGasPriceFactor) {
            this.logger.warn('Environment not set');
            return gasPriceFromNode;
        }
        const gasPriceActual = (0, Web3JSUtils_1.toBN)(gasPriceFromNode)
            .muln(this.environment.getGasPriceFactor);
        return gasPriceActual.toString();
    }
    async getFeeHistory(blockCount, lastBlock, rewardPercentiles) {
        return await this.provider.send('eth_feeHistory', [blockCount, lastBlock, rewardPercentiles]);
    }
    async getGasFees(blockCount, rewardPercentile) {
        if (this.transactionType === TransactionType_1.TransactionType.LEGACY) {
            const gasPrice = await this.getGasPrice();
            return { baseFeePerGas: gasPrice, priorityFeePerGas: gasPrice };
        }
        const networkHistoryFees = await this.getFeeHistory((0, Web3JSUtils_1.toHex)(blockCount), 'pending', [rewardPercentile]);
        const baseFeePerGas = networkHistoryFees.baseFeePerGas[0];
        const priorityFeePerGas = (0, Utils_1.averageBN)(networkHistoryFees.reward.map(rewards => rewards[0]).map(Web3JSUtils_1.toBN)).toString();
        return { baseFeePerGas, priorityFeePerGas };
    }
    async getTransactionCount(address, defaultBlock) {
        return await this.provider.getTransactionCount(address, defaultBlock);
    }
    async getTransaction(transactionHash) {
        return await this.provider.getTransaction(transactionHash);
    }
    async getBlock(blockHashOrBlockNumber) {
        const block = await this.provider.getBlock(blockHashOrBlockNumber);
        if (block == null) {
            throw new Error('Failed to getBlock!');
        }
        return block;
    }
    validateAddress(address, exceptionTitle = 'invalid address:') {
        if (!ethers_1.ethers.utils.isAddress(address)) {
            throw new Error(exceptionTitle + ' ' + address);
        }
    }
    async getCode(address) {
        return await this.provider.getCode(address);
    }
    getNetworkId() {
        if (this.networkId == null) {
            throw new Error('_init not called');
        }
        return this.networkId;
    }
    async isContractDeployed(address) {
        const code = await this.getCode(address);
        return code !== '0x';
    }
    async workerToManager(worker) {
        return await this.relayHubInstance.getWorkerManager(worker);
    }
    async getMinimumStakePerToken(tokenAddress) {
        return await this.relayHubInstance.getMinimumStakePerToken(tokenAddress);
    }
    /**
     * Gets balance of an address on the current RelayHub.
     * @param address - can be a Paymaster or a Relay Manger
     */
    async hubBalanceOf(address) {
        return await this.relayHubInstance.balanceOf(address);
    }
    /**
     * Gets stake of an address on the current StakeManager.
     * @param managerAddress
     */
    async getStakeInfo(managerAddress) {
        const getStakeInfoResult = await this.stakeManagerInstance.getStakeInfo(managerAddress);
        // @ts-ignore - TypeChain generates incorrect type declarations for tuples
        return getStakeInfoResult[0];
    }
    async isRelayManagerStakedOnHub(relayManager) {
        const res = await new Promise((resolve) => {
            const iface = new abi_1.Interface(this.relayHubInstance.contract.interface.fragments);
            const data = iface.encodeFunctionData('verifyRelayManagerStaked', [relayManager]);
            const params = [
                {
                    to: this.relayHubInstance.address,
                    data
                },
                'latest'
            ];
            this.provider.send('eth_call', params)
                .then(res => {
                resolve({
                    isStaked: res === '0x',
                    errorMessage: null
                });
            })
                .catch(err => {
                const errorMessage = this._decodeRevertFromResponse(err);
                resolve({
                    isStaked: false,
                    errorMessage
                });
            });
        });
        return res;
    }
    async initDeployment(deployment) {
        this.deployment = deployment;
        await this._initializeContracts();
    }
    getDeployment() {
        if (this.deployment == null) {
            throw new Error('Contracts deployment is not initialized for Contract Interactor!');
        }
        return this.deployment;
    }
    /**
     * Broadcast the "raw" signed hex-encoded Ethereum transaction.
     * This method sends the RPC call directly
     * @param signedTransaction - the raw signed transaction to broadcast
     */
    async broadcastTransaction(signedTransaction) {
        return await this.provider.send('eth_sendRawTransaction', [signedTransaction]);
    }
    async hubDepositFor(paymaster, transactionDetails) {
        return await this.relayHubInstance.depositFor(paymaster, transactionDetails);
    }
    async resolveDeploymentVersions() {
        const versionsMap = {};
        if (this.deployment.relayHubAddress != null) {
            versionsMap[this.deployment.relayHubAddress] = await this.relayHubInstance.versionHub();
        }
        if (this.deployment.penalizerAddress != null) {
            versionsMap[this.deployment.penalizerAddress] = await this.penalizerInstance.versionPenalizer();
        }
        if (this.deployment.stakeManagerAddress != null) {
            versionsMap[this.deployment.stakeManagerAddress] = await this.stakeManagerInstance.versionSM();
        }
        return versionsMap;
    }
    async queryDeploymentBalances() {
        const balances = {};
        if (this.deployment.relayHubAddress != null) {
            balances[this.deployment.relayHubAddress] = await this.getBalance(this.deployment.relayHubAddress);
        }
        if (this.deployment.penalizerAddress != null) {
            balances[this.deployment.penalizerAddress] = await this.getBalance(this.deployment.penalizerAddress);
        }
        if (this.deployment.stakeManagerAddress != null) {
            balances[this.deployment.stakeManagerAddress] = await this.getBalance(this.deployment.stakeManagerAddress);
        }
        return balances;
    }
    async _hubStakeManagerAddress() {
        return await this.relayHubInstance.getStakeManager();
    }
    stakeManagerAddress() {
        return this.stakeManagerInstance.address;
    }
    async _hubPenalizerAddress() {
        return await this.relayHubInstance.getPenalizer();
    }
    async _hubRelayRegistrarAddress() {
        return await this.relayHubInstance.getRelayRegistrar();
    }
    penalizerAddress() {
        return this.penalizerInstance.address;
    }
    async getRelayRegistrationMaxAge() {
        return await this.relayRegistrar.getRelayRegistrationMaxAge();
    }
    async getRelayInfo(relayManagerAddress) {
        const relayInfo = await this.relayRegistrar.getRelayInfo(this.relayHubInstance.address, relayManagerAddress);
        return Object.assign({}, relayInfo, { relayUrl: (0, Utils_1.packRelayUrlForRegistrar)(relayInfo.urlParts) });
    }
    /**
     * get registered relayers from registrar
     * (output format matches event info)
     */
    async getRegisteredRelays() {
        if (this.relayRegistrar == null) {
            throw new Error('Relay Registrar is not initialized');
        }
        const relayHub = this.relayHubInstance.address;
        if (relayHub == null) {
            throw new Error('RelayHub is not initialized!');
        }
        const relayInfos = await this.relayRegistrar.readRelayInfos(relayHub);
        return relayInfos.map(info => {
            return Object.assign({}, info, {
                relayUrl: (0, Utils_1.packRelayUrlForRegistrar)(info.urlParts)
            });
        });
    }
    async getCreationBlockFromRelayHub() {
        return await this.relayHubInstance.getCreationBlock();
    }
    async getRegisteredWorkers(managerAddress) {
        const topics = (0, Utils_1.address2topic)(managerAddress);
        const workersAddedEvents = await this.getPastEventsForHub([topics], { fromBlock: 1 }, [GSNContractsDataTypes_1.RelayWorkersAdded]);
        return workersAddedEvents.map(it => it.args.newRelayWorkers).flat();
    }
    /**
     *
     * @param paymasterAddress
     * @param workerAddress
     * @param maxFeePerGas
     * @param maxViewableGasLimit
     * @param minViewableGasLimit
     */
    async calculateDryRunCallGasLimit(paymasterAddress, workerAddress, maxFeePerGas, maxViewableGasLimit, minViewableGasLimit) {
        const paymasterBalance = await this.hubBalanceOf(paymasterAddress);
        let workerBalance = Constants_1.constants.MAX_UINT256; // skipping worker address balance check in dry-run
        let workerBalanceMessage = '';
        if (!(0, Utils_1.isSameAddress)(workerAddress, Constants_1.constants.DRY_RUN_ADDRESS)) {
            const workerBalanceStr = await this.getBalance(workerAddress, 'pending');
            workerBalance = (0, Web3JSUtils_1.toBN)(workerBalanceStr);
            workerBalanceMessage = `Worker balance: ${workerBalance.toString()}\n`;
        }
        const smallerBalance = bn_js_1.default.min(paymasterBalance, workerBalance);
        const pctRelayFeeDev = (0, Web3JSUtils_1.toBN)(this.relayHubConfiguration.pctRelayFee.toString()).addn(100);
        const smallerBalanceGasLimit = smallerBalance.div(maxFeePerGas)
            .muln(100)
            .div(pctRelayFeeDev)
            .muln(3).divn(4); // hard-coded to use 75% of available worker/paymaster balance
        const blockGasLimitNum = await this.getBlockGasLimit();
        const blockGasLimit = (0, Web3JSUtils_1.toBN)(blockGasLimitNum)
            .muln(3).divn(4); // hard-coded to use 75% of available block gas limit
        const warningMessage = workerBalanceMessage +
            `Paymaster balance: ${paymasterBalance.toString()}\n` +
            `This is only enough for ${smallerBalanceGasLimit.toString()} gas for a view call at ${maxFeePerGas.toString()} per gas.\n` +
            `Also, block gas limit is: ${blockGasLimit.toString()}\n`;
        if (smallerBalanceGasLimit.lt(minViewableGasLimit)) {
            this.logger.warn(warningMessage +
                `Limiting the view call to minViewableGasLimit (${minViewableGasLimit.toString()}) but successful relaying is not likely.`);
            return minViewableGasLimit;
        }
        const minimalLimit = bn_js_1.default.min(maxViewableGasLimit, bn_js_1.default.min(smallerBalanceGasLimit, blockGasLimit));
        if (minimalLimit.eq(smallerBalanceGasLimit)) {
            this.logger.warn(warningMessage);
        }
        return minimalLimit;
    }
}
exports.ContractInteractor = ContractInteractor;
/**
 * Ganache does not seem to enforce EIP-155 signature. Buidler does, though.
 * This is how {@link Transaction} constructor allows support for custom and private network.
 * @param chainId
 * @param networkId
 * @param chain
 * @return {{common: Common}}
 */
function getRawTxOptions(chainId, networkId, chain) {
    if (chain == null || chain === 'main' || chain === 'private') {
        chain = 'mainnet';
    }
    return {
        common: common_1.default.forCustomChain(chain, {
            chainId,
            networkId
        }, 'london')
    };
}
exports.getRawTxOptions = getRawTxOptions;
//# sourceMappingURL=ContractInteractor.js.map